import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import { Add, Delete, Save, Close, Edit } from '@mui/icons-material';

const AssignTeam = () => {
  const [header, setHeader] = useState({
    projectRef: '',
    projectName: '',
    siteAddress: '',
    scheduledDate: '',
    shift: 'General',
    includeSafetyBriefing: true,
    notes: '',
  });

  const [rows, setRows] = useState([
    { id: 1, memberName: 'John Doe', role: 'Technician', contact: '+91-9000000000' },
  ]);
  const [editingId, setEditingId] = useState(null);

  const [tags, setTags] = useState({ skills: ['Electrical', 'Rooftop'], tools: ['Drill', 'Harness'] });

  const handleHeader = (k, v) => setHeader(prev => ({ ...prev, [k]: v }));

  const addRow = () => {
    const newId = Date.now();
    setRows(prev => ([...prev, { id: newId, memberName: '', role: 'Technician', contact: '' }]));
    setEditingId(newId);
  };
  const updateRow = (id, k, v) => setRows(prev => prev.map(r => r.id === id ? { ...r, [k]: v } : r));
  const deleteRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  const removeTag = (group, value) => setTags(prev => ({ ...prev, [group]: prev[group].filter(t => t !== value) }));

  const handleSubmit = () => {
    if (!header.projectName || !header.scheduledDate || rows.length === 0) {
      alert('Please fill required fields: Project, Date and at least one member');
      return;
    }
    const payload = { ...header, team: rows, tags };
    console.log('Assign Team payload:', payload);
    alert('Team assignment saved (demo)');
    setEditingId(null);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Assign Technicians / Managers
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Quick assignment of team members, shift and essential tags
        </Typography>
      </Box>

      {/* Assignment Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField size="small" fullWidth label="Project Reference" value={header.projectRef} onChange={(e) => handleHeader('projectRef', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" fullWidth required label="Project Name" value={header.projectName} onChange={(e) => handleHeader('projectName', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" fullWidth label="Site Address" value={header.siteAddress} onChange={(e) => handleHeader('siteAddress', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" fullWidth required type="date" label="Scheduled Date" InputLabelProps={{ shrink: true }} value={header.scheduledDate} onChange={(e) => handleHeader('scheduledDate', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Shift</InputLabel>
                <Select value={header.shift} label="Shift" onChange={(e) => handleHeader('shift', e.target.value)}>
                  <MenuItem value="General">General</MenuItem>
                  <MenuItem value="Morning">Morning</MenuItem>
                  <MenuItem value="Evening">Evening</MenuItem>
                  <MenuItem value="Night">Night</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={header.includeSafetyBriefing} onChange={(e) => handleHeader('includeSafetyBriefing', e.target.checked)} color="success" />} label="Include Safety Briefing" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Skills / Tools Tags */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Job Tags</Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">Skills</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {tags.skills.map(t => (
                  <Chip key={t} label={t} onDelete={() => removeTag('skills', t)} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">Tools</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {tags.tools.map(t => (
                  <Chip key={t} label={t} onDelete={() => removeTag('tools', t)} />
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Team Table */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Team Members</Typography>
            <Button variant="contained" size="small" startIcon={<Add />} onClick={addRow} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Assign Technician</Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Member</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Contact</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ minWidth: 240 }}>
                      {editingId === row.id ? (
                        <TextField fullWidth size="small" placeholder="Member Name" value={row.memberName} onChange={(e) => updateRow(row.id, 'memberName', e.target.value)} />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.memberName || '-'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      {editingId === row.id ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Role</InputLabel>
                          <Select value={row.role} label="Role" onChange={(e) => updateRow(row.id, 'role', e.target.value)}>
                            <MenuItem value="Technician">Technician</MenuItem>
                            <MenuItem value="Manager">Manager</MenuItem>
                            <MenuItem value="Supervisor">Supervisor</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.role}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      {editingId === row.id ? (
                        <TextField fullWidth size="small" placeholder="Contact" value={row.contact} onChange={(e) => updateRow(row.id, 'contact', e.target.value)} />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.contact || '-'}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 120 }}>
                      {editingId === row.id ? (
                        <>
                          <Tooltip title="Done">
                            <IconButton onClick={() => setEditingId(null)} size="small" color="primary"><Save fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton onClick={() => deleteRow(row.id)} size="small" color="error"><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => setEditingId(row.id)} size="small"><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton onClick={() => deleteRow(row.id)} size="small" color="error"><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Notes & Actions */}
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={header.notes} onChange={(e) => handleHeader('notes', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Button variant="outlined" startIcon={<Close />}>Cancel</Button>
                <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Save</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AssignTeam;


